import React, { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { obtenerTurnoAnses } from '../services/turnoAnsesService';

// Modal de detalle de un turno ANSES, reutilizable en el listado de Turnos ANSES y en las tarjetas
// de la cola de atención / turnero. Muestra todo lo que se registra del turno (poder, documentación
// a firmar, UDAI, citación, observaciones, presentaciones). Sigue la estética de los otros modales
// ANSES (clase anses-modal).
//
// Props:
//  - idTurnoAnses: id a mostrar (si es null, el modal está cerrado)
//  - turnoPreload: opcional, datos que ya tenemos para mostrar de inmediato mientras se recarga
//  - onClose: () => void
// Espejo del enum ResultadoPresentacion. Antes tenia ACEPTADO y REALIZADO —que no existen— y le
// faltaban NO_PRESENTADO y los dos de cierre, que caian al gris de "sin resultado".
const RESULTADO_BADGE = {
    PENDIENTE: 'bg-info',
    INGRESADO: 'bg-primary',
    CONDICIONAL: 'bg-warning text-dark',
    RECHAZADO: 'bg-danger',
    NO_PRESENTADO: 'bg-secondary',
    FAVORABLE: 'bg-success',
    DESFAVORABLE: 'bg-danger',
};

const RESULTADO_LABEL = {
    PENDIENTE: 'Pendiente', INGRESADO: 'Ingresado', CONDICIONAL: 'Condicional',
    RECHAZADO: 'Rechazado', NO_PRESENTADO: 'No presentado',
    FAVORABLE: 'Favorable', DESFAVORABLE: 'Desfavorable',
};

function formatearFecha(fechaISO) {
    if (!fechaISO) return '—';
    const d = new Date(fechaISO + 'T00:00:00');
    return d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function ModalDetalleTurnoAnses({ idTurnoAnses, turnoPreload = null, onClose }) {
    const [detalle, setDetalle] = useState(turnoPreload);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!idTurnoAnses) return;
        let activo = true;
        setDetalle(turnoPreload);
        setError('');
        setCargando(true);
        obtenerTurnoAnses(idTurnoAnses)
            .then((data) => { if (activo) setDetalle(data); })
            .catch((e) => { if (activo) setError(e?.response?.data?.mensaje || 'No se pudo cargar el detalle.'); })
            .finally(() => { if (activo) setCargando(false); });
        return () => { activo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idTurnoAnses]);

    const d = detalle;

    return (
        <Modal
            show={!!idTurnoAnses}
            onHide={onClose}
            size="lg"
            centered
            scrollable
            className="anses-modal"
            backdropClassName="anses-modal-backdrop"
        >
            <Modal.Header closeButton>
                <Modal.Title><i className="bi bi-building me-2"></i>Detalle del turno ANSES</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <div className="alert alert-danger py-2">{error}</div>}
                {d ? (
                    <>
                        <div className="anses-seccion">
                            <div className="anses-seccion-titulo"><i className="bi bi-person-vcard"></i>Cliente</div>
                            <div className="fw-semibold">{d.apellidoCliente}, {d.nombreCliente}</div>
                            {d.cuil && <small className="text-muted">CUIL {d.cuil}</small>}
                        </div>

                        <div className="anses-seccion">
                            <div className="anses-seccion-titulo"><i className="bi bi-calendar-event"></i>Turno</div>
                            <div className="row g-2">
                                <div className="col-sm-6"><small className="text-muted d-block">Fecha</small>{formatearFecha(d.fecha)}</div>
                                <div className="col-sm-6"><small className="text-muted d-block">Estado</small>{d.estado}</div>
                                {d.tieneTurnoOficial && <div className="col-sm-6"><small className="text-muted d-block">Hora en ANSES</small>{d.horaAnses || '—'}</div>}
                                <div className="col-sm-6"><small className="text-muted d-block">Turno en el estudio</small>{d.horaCitacionEstudio || '—'}{d.citacionAjustada ? ' (ajustada)' : ''}</div>
                                <div className="col-sm-6"><small className="text-muted d-block">UDAI</small>{d.udaiLugar || 'Sin UDAI'}</div>
                                {/* El turno en la escribania (Reajuste): va despues del de ANSES. */}
                                {d.horaEscribania && (
                                    <div className="col-sm-6">
                                        <small className="text-muted d-block">Escribanía</small>
                                        {d.horaEscribania}
                                        {d.escribaniaObservacion && (
                                            <div className="text-muted small">{d.escribaniaObservacion}</div>
                                        )}
                                    </div>
                                )}
                                <div className="col-sm-6"><small className="text-muted d-block">Turno del organismo</small>{d.tieneTurnoOficial ? 'Sí' : 'No (va igual)'}</div>
                                {d.intento > 1 && <div className="col-sm-6"><small className="text-muted d-block">Intento</small>{d.intento}°</div>}
                            </div>
                        </div>

                        <div className="anses-seccion">
                            <div className="anses-seccion-titulo"><i className="bi bi-person-badge"></i>Quién se presenta (poder)</div>
                            {d.usuarioApoderadoNombre ? (
                                <div>Abogado del estudio con poder: <strong>{d.usuarioApoderadoNombre}</strong></div>
                            ) : d.apoderadoNombre ? (
                                <div>
                                    <div><strong>{d.apoderadoNombre}</strong>{d.apoderadoRelacion ? ` · ${d.apoderadoRelacion}` : ''}</div>
                                    {d.apoderadoDni && <small className="text-muted">DNI {d.apoderadoDni}</small>}
                                </div>
                            ) : (
                                <div className="text-muted">Se presenta el titular.</div>
                            )}
                        </div>

                        <div className="anses-seccion">
                            <div className="anses-seccion-titulo"><i className="bi bi-pen"></i>Documentación / firma</div>
                            {d.requiereFirma ? (
                                <>
                                    <div>Requiere firma — {d.firmaResuelta ? 'resuelta' : 'pendiente'}</div>
                                    {d.documentacionAFirmar && <div className="mt-1"><small className="text-muted d-block">A firmar</small>{d.documentacionAFirmar}</div>}
                                    {d.firmaObservacion && <div className="mt-1"><small className="text-muted d-block">Observación de firma</small>{d.firmaObservacion}</div>}
                                </>
                            ) : (
                                <div className="text-muted">No requiere firma.</div>
                            )}
                        </div>

                        {d.observaciones && (
                            <div className="anses-seccion">
                                <div className="anses-seccion-titulo"><i className="bi bi-chat-left-text"></i>Observaciones</div>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{d.observaciones}</div>
                            </div>
                        )}

                        {(d.presentaciones || []).length > 0 && (
                            <div className="anses-seccion">
                                <div className="anses-seccion-titulo"><i className="bi bi-folder"></i>Presentaciones</div>
                                <div className="d-flex flex-wrap gap-2">
                                    {d.presentaciones.map((p) => (
                                        <span key={p.id} className="anses-presentacion">
                                            <span className="anses-presentacion-tipo">{p.tipoTramite}</span>
                                            <span className="anses-presentacion-ref">{p.idTramite ? `#${p.idTramite}` : 'sin carpeta'}</span>
                                            <span className={`badge rounded-pill ${RESULTADO_BADGE[p.resultado] || 'bg-secondary'}`}>{RESULTADO_LABEL[p.resultado] || p.resultado}</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                        {cargando && <div className="text-muted small"><Spinner animation="border" size="sm" className="me-1" />Actualizando…</div>}
                    </>
                ) : (
                    <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" className="rounded-pill" onClick={onClose}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
}
